import React, { useState, useEffect } from 'react';
import { observer, inject } from 'mobx-react';
import _ from 'lodash';
import { displayError } from '@aws-ee/base-ui/dist/helpers/notification';
import EnvironmentStatus from '../../../../../models/EnvironmentStatus';
import launchProgress from './launchProgress';

const MaxShortTicksCount = 5;
const ShortTickPeriod = 5 * 1000;
const LongTickPeriod = 30 * 1000;
const ActionButton = ({
  studyId,
  enableWorkspace,
  workspace,
  disabled,
  onStartClick,
  onConnectClick,
  scEnvironmentsStore,
  currentUserScEnvironmentsStore,
}) => {
  const [buttonInProgress, setButtonInProgress] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [warm, setWarm] = useState(null);
  const [, setTick] = useState(0);
  const inProgress =
    buttonInProgress ||
    workspace.status === EnvironmentStatus.Pending ||
    workspace.status === EnvironmentStatus.Starting;

  useEffect(() => {
    if (workspace.status) setButtonInProgress(false);
  }, [workspace.status]);


  useEffect(() => {
    if (!inProgress) {
      setStartedAt(null);
      setWarm(null);
      return undefined;
    }
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [inProgress]);

  const handleAction = async fn => {
    try {
      await fn();
      currentUserScEnvironmentsStore.startHeartbeat(ShortTickPeriod, LongTickPeriod, MaxShortTicksCount);
      currentUserScEnvironmentsStore.load();
    } catch (error) {
      displayError(error);
      setButtonInProgress(false);
      setStartedAt(null);
      setWarm(null);
    }
  };

  const handleStart = async () => {
    setButtonInProgress(true);
    setStartedAt(Date.now());
    await handleAction(async () => {
      if (!_.isNil(onStartClick)) await onStartClick();
      let response;
      if (!_.isNil(workspace.status)) {
        response = await scEnvironmentsStore.startScContainerEnvironment(workspace.id);
      } else {
        response = await scEnvironmentsStore.launchScStatelessContainerEnvironment(studyId);
      }
      if (!_.isNil(_.get(response, 'status'))) setWarm(response.status === EnvironmentStatus.Running);
    });
  };

  const handleConnect = async () => {
    setButtonInProgress(true);
    setStartedAt(Date.now());
    await handleAction(async () => {
      if (!_.isNil(onConnectClick)) await onConnectClick();

      const url = workspace.url;
      window.open(url, '_blank', 'noopener,noreferrer');
    });
    setButtonInProgress(false);
  };

  if (enableWorkspace === false) {
    return '';
  }
  if (inProgress) {
    const { label, detail } = launchProgress({
      status: workspace.status,
      warm,
      elapsedSeconds: _.isNil(startedAt) ? null : Math.max(0, Math.floor((Date.now() - startedAt) / 1000)),
    });
    return (
      <button type="button" className="progress launch-progress">
        <div className="mr2">
          <i className="sync loading icon" />
        </div>
        {/* spans, not divs: button.progress styles every descendant div as the spinner chip */}
        <span className="launch-progress-text">
          <span className="launch-progress-label">{label}</span>
          {detail && <span className="launch-progress-detail">{detail}</span>}
        </span>
      </button>
    );
  }
  if (_.isNil(workspace.status)) {
    return (
      <button type="button" className={`primary-black ${disabled ? 'disabled' : ''}`} onClick={handleStart}>
        {disabled ? (
          <span role="img" aria-label="lock">
            🔒
          </span>
        ) : (
          <span role="img" aria-label="rocket">
            🚀
          </span>
        )}{' '}
        Start Module
      </button>
    );
  }
  if (workspace.status === EnvironmentStatus.Stopped) {
    return (
      <button type="button" className={`primary-black ${disabled ? 'disabled' : ''}`} onClick={handleStart}>
        {disabled ? (
          <span role="img" aria-label="lock">
            🔒
          </span>
        ) : (
          <span role="img" aria-label="rocket">
            🚀
          </span>
        )}{' '}
        Start Module
      </button>
    );
  }
  if (workspace.status === EnvironmentStatus.Running) {
    return (
      <button type="button" className={`primary-blue ${disabled ? 'disabled' : ''}`} onClick={handleConnect}>
        {disabled ? (
          <span role="img" aria-label="lock">
            🔒
          </span>
        ) : (
          <span role="img" aria-label="rocket">
            ✨
          </span>
        )}{' '}
        Launch Module
      </button>
    );
  }
  return null;
};

export default inject('scEnvironmentsStore', 'currentUserScEnvironmentsStore')(observer(ActionButton));
